import * as React from "react";
import { Check, X, ChevronDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { cn } from "./utils";
import { Badge } from "./badge";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export type Option = {
  value: string;
  label: string;
  isInactive?: boolean;
};

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  badgeClassName?: string;
  maxDisplayTags?: number;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options",
  emptyMessage = "No options found.",
  className,
  badgeClassName,
  maxDisplayTags = 1
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleUnselect = (value: string) => {
    onChange(selected.filter((item) => item !== value));
  };

  const handleSelect = (value: string) => {
    onChange([...selected, value]);
  };

  const selectedOptions = options.filter(opt => selected.includes(opt.value));
  const displayOptions = selectedOptions.slice(0, maxDisplayTags);
  const hiddenCount = selectedOptions.length - displayOptions.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-full min-h-10 items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-[13px] md:text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-pointer",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selected.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
            {displayOptions.map((opt) => (
              <Badge
                key={opt.value}
                variant="secondary"
                className={cn(
                  "truncate max-w-[150px] md:max-w-[300px] px-2 text-[11px] md:text-xs font-normal flex items-center gap-1", 
                  opt.isInactive ? "bg-gray-100 text-gray-600 border-gray-300 border-solid" : badgeClassName
                )}
              >
                <span className="truncate">{opt.label}</span>
                {!opt.isInactive && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnselect(opt.value);
                    }}
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-slate-200 p-0.5"
                  >
                    <X className="h-2 w-2 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </Badge>
            ))}
            {hiddenCount > 0 && (
              <Badge variant="secondary" className={cn("text-[11px] md:text-xs font-normal", badgeClassName)}>
                +{hiddenCount}<span className="hidden md:inline ml-1">more</span>
              </Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
        <Command
          className="bg-transparent"
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput
            placeholder="Search..."
            className="border-none focus:ring-0 px-4 py-2 text-sm focus:outline-none"
          />
          <CommandList className="max-h-64" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    if (option.isInactive) return;
                    if (selected.includes(option.value)) {
                      handleUnselect(option.value);
                    } else {
                      handleSelect(option.value);
                    }
                  }}
                  className={cn(
                    "px-2 py-1.5",
                    !option.isInactive && "cursor-pointer hover:bg-gray-100 aria-selected:bg-gray-100",
                    option.isInactive && "text-gray-600 bg-gray-50/50 cursor-not-allowed"
                  )}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(option.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
